import { useEffect, useMemo, useState } from "react";
import { Box, Divider, MenuItem, TextField } from "@mui/material";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import { validateCode, validateRequired } from "./common/validation";
import { toast } from "react-toastify";
import {
  antigenApi,
  riskCohortApi,
  scheduleApi,
  vaccineProductApi,
} from "api/immunization";
import {
  SCHEDULE_ANCHORS,
  SCHEDULE_TYPES,
  SCHEDULE_TYPE_FORCED_ANCHOR,
  SEX_ELIGIBILITY,
  humanise,
} from "config/immunizationConstants";
import DoseRuleEditor from "./DoseRuleEditor";

const numberOrNull = (value) => (value === "" ? null : Number(value));

const ScheduleModal = ({ open, onClose, data }) => {
  const scheduleId = data?._id;
  const isUpdate = Boolean(scheduleId);

  const [form, setForm] = useState({});
  const [antigens, setAntigens] = useState([]);
  const [products, setProducts] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!open) return;

    const hydrate = (record) =>
      setForm({
        code: record?.code || "",
        scheduleName: record?.scheduleName || "",
        description: record?.description || "",
        jurisdiction: record?.jurisdiction || "",
        issuingAuthority: record?.issuingAuthority || "",
        scheduleType: record?.scheduleType || "ROUTINE_PEDIATRIC",
        anchorType: record?.anchorType || "BIRTH",
        targetSex: record?.targetSex || "ANY",
        targetMinAgeDays: record?.targetMinAgeDays ?? "",
        targetMaxAgeDays: record?.targetMaxAgeDays ?? "",
        precedence: record?.precedence ?? 100,
        sequence: record?.sequence ?? 0,
        doseRules: (record?.doseRules || []).map((rule) => ({
          ...rule,
          antigenIds: (rule.antigenIds || []).map((a) =>
            typeof a === "object" ? a._id : a,
          ),
          permittedProductIds: (rule.permittedProductIds || []).map((p) =>
            typeof p === "object" ? p._id : p,
          ),
          eligibility: {
            sex: rule.eligibility?.sex || "ANY",
            riskCohortIds: (rule.eligibility?.riskCohortIds || []).map((c) =>
              typeof c === "object" ? c._id : c,
            ),
            pregnancyOnly: Boolean(rule.eligibility?.pregnancyOnly),
            occupationCodes: rule.eligibility?.occupationCodes || [],
          },
          recommendedOffsetDays: rule.recommendedOffsetDays ?? "",
          minAgeDays: rule.minAgeDays ?? "",
          maxAgeDays: rule.maxAgeDays ?? "",
          earliestValidAgeDays: rule.earliestValidAgeDays ?? "",
          minIntervalDays: rule.minIntervalDays ?? "",
          recommendedIntervalDays: rule.recommendedIntervalDays ?? "",
          overdueAfterDays: rule.overdueAfterDays ?? "",
        })),
      });

    if (scheduleId) {
      setLoadingDetail(true);
      scheduleApi
        .byId(scheduleId)
        .then((res) => hydrate(res?.data?.data || data))
        .catch(() => {
          toast.error("Could not load the full schedule");
          hydrate(data);
        })
        .finally(() => setLoadingDetail(false));
    } else {
      hydrate({});
    }
  }, [open, data, scheduleId]);

  useEffect(() => {
    if (!open) return;

    antigenApi
      .active()
      .then((res) => setAntigens(res?.data?.data || []))
      .catch(() => toast.error("Could not load antigens"));
    vaccineProductApi
      .active()
      .then((res) => setProducts(res?.data?.data || []))
      .catch(() => toast.error("Could not load vaccine products"));
    riskCohortApi
      .active()
      .then((res) => setCohorts(res?.data?.data || []))
      .catch(() => toast.error("Could not load risk cohorts"));
  }, [open]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const forcedAnchor = SCHEDULE_TYPE_FORCED_ANCHOR[form.scheduleType];
  const rules = form.doseRules || [];

  const validationError = useMemo(() => {
    const base =
      validateCode(form.code, { isUpdate, label: "Schedule code" }) ||
      validateRequired(form.scheduleName, "Schedule name") ||
      validateRequired(form.scheduleType, "Schedule type");
    if (base) return base;

    if (
      form.targetMinAgeDays !== "" &&
      form.targetMaxAgeDays !== "" &&
      Number(form.targetMaxAgeDays) < Number(form.targetMinAgeDays)
    ) {
      return "Target maximum age must not be below the target minimum age";
    }

    const seenCodes = new Set();
    for (let i = 0; i < rules.length; i += 1) {
      const rule = rules[i];
      const position = rule.ruleCode || `Rule ${i + 1}`;

      if (!String(rule.ruleCode || "").trim()) {
        return `Rule ${i + 1}: a rule code is required`;
      }
      const key = String(rule.ruleCode).trim().toUpperCase();
      if (seenCodes.has(key)) return `Duplicate rule code "${rule.ruleCode}"`;
      seenCodes.add(key);

      if (!rule.antigenIds?.length) {
        return `${position}: at least one antigen is required`;
      }
      if (!rule.doseSequence || Number(rule.doseSequence) < 1) {
        return `${position}: dose sequence must be 1 or more`;
      }
      if (
        rule.minAgeDays !== "" &&
        rule.maxAgeDays !== "" &&
        Number(rule.maxAgeDays) < Number(rule.minAgeDays)
      ) {
        return `${position}: maximum age must not be below the minimum age`;
      }
      if (
        rule.earliestValidAgeDays !== "" &&
        rule.minAgeDays !== "" &&
        Number(rule.earliestValidAgeDays) > Number(rule.minAgeDays)
      ) {
        return `${position}: earliest valid age cannot be later than the minimum age`;
      }
      if (
        rule.minIntervalDays !== "" &&
        rule.recommendedIntervalDays !== "" &&
        Number(rule.minIntervalDays) > Number(rule.recommendedIntervalDays)
      ) {
        return `${position}: minimum interval cannot exceed the recommended interval`;
      }
      if (Number(rule.doseSequence) === 1 && rule.minIntervalDays !== "") {
        return `${position}: the first dose cannot define an interval from a previous dose`;
      }
      if (rule.eligibility?.pregnancyOnly && rule.eligibility?.sex === "MALE") {
        return `${position}: a pregnancy-only rule cannot be male-only`;
      }
    }

    return "";
  }, [form, rules, isUpdate]);

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      scheduleName: form.scheduleName.trim(),
      description: form.description?.trim() || "",
      jurisdiction: form.jurisdiction?.trim() || "",
      issuingAuthority: form.issuingAuthority?.trim() || "",
      scheduleType: form.scheduleType,
      anchorType: forcedAnchor || form.anchorType,
      targetSex: form.targetSex,
      targetMinAgeDays: numberOrNull(form.targetMinAgeDays),
      targetMaxAgeDays: numberOrNull(form.targetMaxAgeDays),
      precedence: Number(form.precedence) || 0,
      sequence: Number(form.sequence) || 0,
      doseRules: rules.map((rule) => ({
        ruleCode: rule.ruleCode.trim(),
        label: rule.label?.trim() || "",
        antigenIds: rule.antigenIds,
        doseSequence: Number(rule.doseSequence),
        recommendedOffsetDays: numberOrNull(rule.recommendedOffsetDays),
        minAgeDays: numberOrNull(rule.minAgeDays),
        maxAgeDays: numberOrNull(rule.maxAgeDays),
        earliestValidAgeDays: numberOrNull(rule.earliestValidAgeDays),
        minIntervalDays: numberOrNull(rule.minIntervalDays),
        recommendedIntervalDays: numberOrNull(rule.recommendedIntervalDays),
        graceDays: Number(rule.graceDays) || 0,
        overdueAfterDays: numberOrNull(rule.overdueAfterDays),
        startingAgeVariants: rule.startingAgeVariants || [],
        eligibility: {
          sex: rule.eligibility?.sex || "ANY",
          riskCohortIds: rule.eligibility?.riskCohortIds || [],
          pregnancyOnly: Boolean(rule.eligibility?.pregnancyOnly),
          occupationCodes: rule.eligibility?.occupationCodes || [],
        },
        permittedProductIds: rule.permittedProductIds || [],
        contraindicationIds: rule.contraindicationIds || [],
        requirement: rule.requirement || "MANDATORY",
        notes: rule.notes?.trim() || "",
        sequence: Number(rule.sequence) || 0,
      })),
    };

    try {
      if (isUpdate) {
        await scheduleApi.update(scheduleId, payload);
        toast.success("Schedule updated successfully");
      } else {
        await scheduleApi.create({ ...payload, code: form.code.trim() });
        toast.success("Schedule created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} schedule`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Schedule"
      saving={saving}
      loading={loadingDetail}
      validationError={validationError}
      onSubmit={handleSubmit}
      maxWidth="lg"
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <CodeField
          value={form.code}
          onChange={(value) => set({ code: value })}
          isUpdate={isUpdate}
          label="Schedule Code"
        />
        <TextField
          label="Schedule Name"
          required
          fullWidth
          size="small"
          value={form.scheduleName || ""}
          onChange={(event) => set({ scheduleName: event.target.value })}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          label="Issuing Authority"
          fullWidth
          size="small"
          value={form.issuingAuthority || ""}
          onChange={(event) => set({ issuingAuthority: event.target.value })}
          helperText="e.g. Govt of India, IAP, ACIP, WHO"
        />
        <TextField
          label="Jurisdiction"
          fullWidth
          size="small"
          value={form.jurisdiction || ""}
          onChange={(event) => set({ jurisdiction: event.target.value })}
        />
      </Box>

      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.description || ""}
        onChange={(event) => set({ description: event.target.value })}
      />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          select
          label="Schedule Type"
          required
          size="small"
          sx={{ minWidth: 210 }}
          value={form.scheduleType || ""}
          onChange={(event) => {
            const scheduleType = event.target.value;
            set({
              scheduleType,
              anchorType:
                SCHEDULE_TYPE_FORCED_ANCHOR[scheduleType] || form.anchorType,
            });
          }}
        >
          {SCHEDULE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {humanise(type)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Anchor Event"
          size="small"
          sx={{ minWidth: 210 }}
          value={forcedAnchor || form.anchorType || "BIRTH"}
          disabled={Boolean(forcedAnchor)}
          onChange={(event) => set({ anchorType: event.target.value })}
          helperText={
            forcedAnchor ?
              `${humanise(form.scheduleType)} always anchors on ${humanise(forcedAnchor)}`
            : "Event the timings are measured from"
          }
        >
          {SCHEDULE_ANCHORS.map((anchor) => (
            <MenuItem key={anchor} value={anchor}>
              {humanise(anchor)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Target Sex"
          size="small"
          sx={{ minWidth: 150 }}
          value={form.targetSex || "ANY"}
          onChange={(event) => set({ targetSex: event.target.value })}
        >
          {SEX_ELIGIBILITY.map((option) => (
            <MenuItem key={option} value={option}>
              {humanise(option)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          label="Target Min Age (days)"
          type="number"
          size="small"
          sx={{ minWidth: 180 }}
          value={form.targetMinAgeDays}
          onChange={(event) => set({ targetMinAgeDays: event.target.value })}
        />
        <TextField
          label="Target Max Age (days)"
          type="number"
          size="small"
          sx={{ minWidth: 180 }}
          value={form.targetMaxAgeDays}
          onChange={(event) => set({ targetMaxAgeDays: event.target.value })}
        />
        <TextField
          label="Precedence"
          type="number"
          size="small"
          sx={{ minWidth: 140 }}
          value={form.precedence}
          onChange={(event) => set({ precedence: event.target.value })}
          helperText="Lower wins"
        />
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
      </Box>

      <Divider />

      <DoseRuleEditor
        value={rules}
        onChange={(next) => set({ doseRules: next })}
        antigens={antigens}
        products={products}
        cohorts={cohorts}
      />
    </MasterModalShell>
  );
};

export default ScheduleModal;
